import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  FloatNumberInput,
  IntNumberInput,
} from "@/shared/components/domain/qpm/inputs/number-input";
import { Plus, Trash2 } from "lucide-react";
import { useMemo } from "react";

export type EditableRentingFeeRow = {
  durationMonths: number;
  monthly: number;
};

export function sortedRentingFeeRows(
  rows: EditableRentingFeeRow[],
): EditableRentingFeeRow[] {
  return [...rows].sort(
    (left, right) => left.durationMonths - right.durationMonths,
  );
}

export function rentingFeeRowsEqual(
  left: EditableRentingFeeRow[],
  right: EditableRentingFeeRow[],
): boolean {
  const sortedLeft = sortedRentingFeeRows(left);
  const sortedRight = sortedRentingFeeRows(right);
  if (sortedLeft.length !== sortedRight.length) {
    return false;
  }
  return sortedLeft.every((leftRow, index) => {
    const rightRow = sortedRight[index];
    return (
      leftRow.durationMonths === rightRow.durationMonths &&
      leftRow.monthly === rightRow.monthly
    );
  });
}

export function getRentingFeeDuplicateMonths(
  rows: EditableRentingFeeRow[],
): Set<number> {
  const counts = new Map<number, number>();
  rows.forEach((row) => {
    if (row.durationMonths <= 0) {
      return;
    }
    counts.set(row.durationMonths, (counts.get(row.durationMonths) ?? 0) + 1);
  });
  return new Set(
    [...counts.entries()]
      .filter(([, count]) => count > 1)
      .map(([durationMonths]) => durationMonths),
  );
}

export function hasInvalidRentingFeeRows(
  rows: EditableRentingFeeRow[],
): boolean {
  return (
    rows.some((row) => row.durationMonths <= 0 || row.monthly < 0) ||
    getRentingFeeDuplicateMonths(rows).size > 0
  );
}

function getSuggestedRentingDurationMonths(
  rows: EditableRentingFeeRow[],
): number {
  const usedMonths = new Set(rows.map((row) => row.durationMonths));
  for (const candidate of [12, 24, 36, 48, 60]) {
    if (!usedMonths.has(candidate)) {
      return candidate;
    }
  }
  if (rows.length === 0) {
    return 12;
  }
  return Math.max(...rows.map((row) => row.durationMonths)) + 12;
}

export function EditableRentingFeesTable({
  rows,
  disabled,
  onChange,
  emptyText = t("Keine Mietpreise vorhanden"),
  addButtonText = t("Zeile hinzufügen"),
}: {
  rows: EditableRentingFeeRow[];
  disabled?: boolean;
  onChange: (rows: EditableRentingFeeRow[]) => void;
  emptyText?: string;
  addButtonText?: string;
}) {
  const duplicateMonths = useMemo(
    () => getRentingFeeDuplicateMonths(rows),
    [rows],
  );

  const updateRow = (index: number, patch: Partial<EditableRentingFeeRow>) => {
    onChange(
      rows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...patch } : row,
      ),
    );
  };

  const removeRow = (index: number) => {
    onChange(rows.filter((_, rowIndex) => rowIndex !== index));
  };

  const addRow = () => {
    onChange([
      ...rows,
      {
        durationMonths: getSuggestedRentingDurationMonths(rows),
        monthly: 0,
      },
    ]);
  };

  return (
    <div className="space-y-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[160px]">{t("Laufzeit")}</TableHead>
            <TableHead className="text-end">{t("Monatlich")}</TableHead>
            <TableHead className="w-[72px] text-right">{t("Aktion")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={`${index}-${row.durationMonths}`}>
              <TableCell className="align-top">
                <IntNumberInput
                  value={row.durationMonths}
                  disabled={disabled}
                  onChange={(durationMonths) =>
                    updateRow(index, { durationMonths })
                  }
                />
                {duplicateMonths.has(row.durationMonths) ? (
                  <div className="text-destructive mt-1 text-xs">
                    {t("Diese Laufzeit ist bereits vorhanden.")}
                  </div>
                ) : null}
                {row.durationMonths <= 0 ? (
                  <div className="text-destructive mt-1 text-xs">
                    {t("Bitte eine Laufzeit größer als 0 angeben.")}
                  </div>
                ) : null}
              </TableCell>
              <TableCell className="align-top">
                <div className="flex justify-end">
                  <FloatNumberInput
                    className="justify-self-end"
                    value={row.monthly}
                    fractions={2}
                    disabled={disabled}
                    onChange={(monthly) => updateRow(index, { monthly })}
                  />
                </div>
                {row.monthly < 0 ? (
                  <div className="text-destructive mt-1 text-right text-xs">
                    {t("Bitte einen Mietpreis ab 0 angeben.")}
                  </div>
                ) : null}
              </TableCell>
              <TableCell className="text-right align-top">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={disabled}
                  onClick={() => removeRow(index)}
                  aria-label={t("Mietpreis entfernen")}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}

          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="text-muted-foreground py-6">
                {emptyText}
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>

      <Button
        type="button"
        onClick={addRow}
        disabled={disabled}
        className="w-full sm:w-auto"
      >
        <Plus className="mr-2 h-4 w-4" />
        {addButtonText}
      </Button>
    </div>
  );
}
